import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, LazyMotion, domAnimation } from "framer-motion";
import { AnimatePresenceWithProps } from "../../types/animation";

// Simple Icon component using FontAwesome CSS classes
const Icon: React.FC<{ name: string; className?: string }> = ({
  name,
  className = "",
}) => {
  return <i className={`fas ${name} ${className}`}></i>;
};

const AdminSidebar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu when clicking outside or navigating
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".sidebar-container") &&
        !target.closest(".mobile-menu-button")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Menu items array for cleaner rendering
  const menuItems = [
    {
      path: "/admin",
      label: "Dashboard",
      icon: <Icon name="fa-tachometer-alt" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/users",
      label: "Users",
      icon: <Icon name="fa-users" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/products",
      label: "Products",
      icon: <Icon name="fa-box" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/categories",
      label: "Categories",
      icon: <Icon name="fa-tags" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/orders",
      label: "Orders",
      icon: <Icon name="fa-shopping-cart" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/content",
      label: "Content Pages",
      icon: <Icon name="fa-file-alt" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/homepage-sections",
      label: "Homepage Sections",
      icon: <Icon name="fa-th-large" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/blog",
      label: "Blog Posts",
      icon: <Icon name="fa-newspaper" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/projects",
      label: "Projects",
      icon: <Icon name="fa-folder-open" className="mr-3 text-xl" />,
    },
    {
      path: "/admin/service-requests",
      label: "Service Requests",
      icon: <Icon name="fa-envelope" className="mr-3 text-xl" />,
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <LazyMotion features={domAnimation}>
      {/* Mobile Menu Button - Position at the bottom instead of top */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-gray-800 z-50 py-2 px-4 flex items-center justify-between shadow-md">
        <button
          className="mobile-menu-button bg-blue-600 text-white p-2 rounded-md shadow-lg flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
          aria-label="Toggle admin menu"
        >
          {isMobileMenuOpen ? (
            <Icon name="fa-times" className="text-xl mr-2" />
          ) : (
            <Icon name="fa-bars" className="text-xl mr-2" />
          )}
          <span>Admin Menu</span>
        </button>
        <div className="text-white font-semibold">Admin Dashboard</div>
      </div>

      {/* Desktop Sidebar - Fixed position for consistent visibility */}
      <aside className="hidden md:block md:w-64 bg-gray-800 text-white p-4 flex-shrink-0 h-full shadow-lg overflow-y-auto">
        <h2 className="text-xl font-semibold mb-6 border-b border-gray-700 pb-4">
          Admin Menu
        </h2>
        <nav>
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center p-2 rounded-md hover:bg-gray-700 transition-colors ${
                    isActive(item.path)
                      ? "bg-blue-600 text-white"
                      : "text-gray-300"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Mobile Sidebar - Higher z-index and improved animation */}
      <AnimatePresenceWithProps mode="wait">
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <motion.aside
              className="sidebar-container w-64 bg-gray-800 text-white p-4 h-full overflow-y-auto shadow-xl"
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6 border-b border-gray-700 pb-4">
                <h2 className="text-xl font-semibold">Admin Menu</h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-400 hover:text-white"
                  aria-label="Close menu"
                >
                  <Icon name="fa-times" className="text-xl" />
                </button>
              </div>
              <nav>
                <ul className="space-y-2">
                  {menuItems.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center p-3 rounded-md hover:bg-gray-700 transition-colors ${
                          isActive(item.path)
                            ? "bg-blue-600 text-white"
                            : "text-gray-300"
                        }`}
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresenceWithProps>

      {/* Compensate for fixed sidebar on desktop and mobile menu at bottom for mobile */}
      <div className="md:pl-64">
        {/* Extra space for mobile to account for the fixed footer */}
        <div className="md:hidden h-14 mb-10"></div>
      </div>
    </LazyMotion>
  );
};

export default AdminSidebar;
